import { defineMessage, MessageDescriptor } from 'react-intl';
import { BaseUseCase } from './usecases';

export type CategoryType = 'crm' | 'online-store' | 'productivity' | 'pms' | 'communication';
export type Category = {
  id: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  solution: string;
  usecases: BaseUseCase[];
};

const categories = [
  {
    id: 'crm',
    title: defineMessage<MessageDescriptor>({ id: 'categories-title-crm', defaultMessage: 'CRM' }),
    description: defineMessage<MessageDescriptor>({
      id: 'categories-description-crm',
      defaultMessage:
        'Customer relationship management is the process of managing interactions with existing as well as past and potential customers. It is one of many different approaches that allow a company to manage and analyse its own interactions with its past, current and potential customers.',
    }),
    solution: 'Reduce manual work in managing your business',
    usecases: [
      {
        id: 'create-invoices',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-create-invoices',
          defaultMessage: 'Easily create invoices from outside of your crm',
        }),
      },
      {
        id: 'create-contacts',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-create-contacts',
          defaultMessage: 'Sync contacts or companies from another app',
        }),
      },
      {
        id: 'create-deals',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-create-deals',
          defaultMessage: 'Manage deals from outside to capture everything inside your crm',
        }),
      },
      {
        id: 'trigger-deals',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-trigger-deals',
          defaultMessage: 'Trigger something in another app when a new deal is created',
        }),
      },
      {
        id: 'trigger-contacts',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-trigger-contacts',
          defaultMessage: 'Create contacts in another app when creating one in your CRM',
        }),
      },
    ],
  },
  {
    id: 'online-store',
    title: defineMessage<MessageDescriptor>({
      id: 'categories-title-online-store',
      defaultMessage: 'Online Store Platform',
    }),
    description: defineMessage<MessageDescriptor>({
      id: 'categories-description-online-store',
      defaultMessage: 'Platforms that offer their customers an easy way to create an online store',
    }),
    solution: 'Remove friction in your online sales process',
    usecases: [
      {
        id: 'trigger-sales',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-trigger-sales',
          defaultMessage:
            'Change something automatically in another app, when you sell something in your webshop',
        }),
      },
      {
        id: 'manage-products',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-manage-products',
          defaultMessage: 'Manage your products',
        }),
      },
      {
        id: 'manage-stock',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-manage-stock',
          defaultMessage: 'Manage your stock',
        }),
      },
    ],
  },
  {
    id: 'productivity',
    title: defineMessage<MessageDescriptor>({
      id: 'categories-title-productivity',
      defaultMessage: 'Productivity software',
    }),
    description: defineMessage<MessageDescriptor>({
      id: 'categories-description-productivity',
      defaultMessage: 'Software that helps you in your day to day business',
    }),
    solution: 'Integrate your daily power tools with your other software solutions',
    usecases: [
      {
        id: 'trigger-from-spreadsheet',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-trigger-from-spreadsheet',
          defaultMessage: 'Use a spreadsheet as data source',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-trigger-from-spreadsheet',
          defaultMessage: '',
        }),
      },
      {
        id: 'trigger-from-filesystem',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-trigger-from-filesystem',
          defaultMessage: 'Use a file as data source to change shomething in another system',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-trigger-from-filesystem',
          defaultMessage: '',
        }),
      },
      {
        id: 'create-doc',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-create-doc',
          defaultMessage: 'Create a document',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-create-doc',
          defaultMessage:
            'Automatically generate a report or another document, based on what happens in other software',
        }),
      },
      {
        id: 'send-email',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-send-email',
          defaultMessage: 'Send an email',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-send-email',
          defaultMessage: 'Automatically send an email, based on what happens in other software',
        }),
      },
    ],
  },
  {
    id: 'communication',
    title: defineMessage<MessageDescriptor>({
      id: 'categories-title-communication',
      defaultMessage: 'Communication software',
    }),
    description: defineMessage<MessageDescriptor>({
      id: 'categories-description-communication',
      defaultMessage: 'Communication tools between people and software',
    }),
    solution: 'Chat with your software',
    usecases: [
      {
        id: 'trigger-hitch',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-trigger-hitch',
          defaultMessage: 'Trigger one of our integrations, right from your chat app',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-trigger-hitch',
          defaultMessage: 'Give commands to our system, right from your chat app',
        }),
      },
      {
        id: 'report-hitch',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-report-hitch',
          defaultMessage: 'Get notified of any activity',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-report-hitch',
          defaultMessage: 'Get reports from our system when something happens within your software',
        }),
      },
    ],
  },
  {
    id: 'pms',
    title: defineMessage<MessageDescriptor>({
      id: 'categories-title-pms',
      defaultMessage: 'Property Management System',
    }),
    description: defineMessage<MessageDescriptor>({
      id: 'categories-description-pms',
      defaultMessage: 'Integrate your property with the rest of your business',
    }),
    solution: 'Avoid duplicate entries',
    usecases: [
      {
        id: 'import-guests',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-import-guests',
          defaultMessage: 'One time import of all contacts',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-import-guests',
          defaultMessage:
            'Take a headstart, by importing all existing customer data from another app',
        }),
      },
      {
        id: 'trigger-booking',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-trigger-booking',
          defaultMessage: 'A new booking in the app triggers a change in another system',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-trigger-booking',
          defaultMessage: 'When a new booking occurs, make sure your CRM is up-to-date',
        }),
      },
      {
        id: 'update-status',
        title: defineMessage<MessageDescriptor>({
          id: 'category-use-case-title-update-status',
          defaultMessage: 'Change booking status',
        }),
        description: defineMessage<MessageDescriptor>({
          id: 'category-use-case-description-update-status',
          defaultMessage:
            'When the deal in your CRM is complete, the booking should become confirmed in your PMS',
        }),
      },
    ],
  },
] as Category[];

export const Categories: Record<Category['id'], Category> = categories.reduce(
  (prevValue, currValue: Category) => {
    return {
      ...prevValue,
      [currValue.id]: currValue,
    };
  },
  {}
);

export default categories;
